import { camelCase, isNull, upperFirst } from 'lodash';

import { COMPONENT_EMPTY_NAME } from 'bom/constants';
import { BomPartType } from 'bom/enums';
import { IBomPart } from 'bom/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { CorporatePartCostStatus } from 'corporate/enums';
import { PartNumberHelper } from 'parts/helpers';
import { PricingHelper } from 'pricing/helpers';

export const BomPartHelper = {
  COST_TYPE: 'cost',
  PURCHASE_COST_TYPE: 'purchaseCost',

  getManufacturer: (bomPart: IBomPart) => {
    if (!bomPart) {
      return null;
    }

    const part = bomPart.partArr;

    if (part?.id) {
      return {
        id: part?.manufacturerId,
        name: part?.manufacturerName,
      };
    }

    if (bomPart.type === BomPartType.COMPONENT && bomPart.manufacturerId) {
      return {
        id: bomPart.manufacturerId,
        name: bomPart.manufacturerName,
      };
    }

    return null;
  },

  hasExpiredCost: (bomPart: IBomPart) => {
    return [CorporatePartCostStatus.EXPIRED, CorporatePartCostStatus.OLD].includes(
      bomPart?.computedCostingStatus
    );
  },

  isPnbPricingEnabled: (bomPart: IBomPart) => {
    return (
      1 === +bomPart?.corporatePartConfig?.pnbPricingEnabled ||
      PricingHelper.isNonEmpty(BomPartHelper.getPurchaseCostBase(bomPart)) ||
      PricingHelper.isNonEmpty(BomPartHelper.getPurchaseCostAdder(bomPart)) ||
      PricingHelper.isNonEmpty(BomPartHelper.getCostBase(bomPart)) ||
      PricingHelper.isNonEmpty(BomPartHelper.getCostAdder(bomPart))
    );
  },

  isBaseAdderPnbPricingEnabled: (bomPart: IBomPart) => {
    return !isNull(bomPart?.partArr?.baseIdentifier) && BomPartHelper.isPnbPricingEnabled(bomPart);
  },

  getPurchaseCostBase: (bomPart: IBomPart): null | number =>
    BomPartHelper.getCostingFieldValue(
      bomPart,
      BomPartHelper.PURCHASE_COST_TYPE,
      PricingHelper.PRICING_TYPE_BASE
    ),

  getPurchaseCostAdder: (bomPart: IBomPart): null | number =>
    BomPartHelper.getCostingFieldValue(
      bomPart,
      BomPartHelper.PURCHASE_COST_TYPE,
      PricingHelper.PRICING_TYPE_ADDER
    ),

  getCostBase: (bomPart: IBomPart): null | number =>
    BomPartHelper.getCostingFieldValue(
      bomPart,
      BomPartHelper.COST_TYPE,
      PricingHelper.PRICING_TYPE_BASE
    ),

  getCostAdder: (bomPart: IBomPart): null | number =>
    BomPartHelper.getCostingFieldValue(
      bomPart,
      BomPartHelper.COST_TYPE,
      PricingHelper.PRICING_TYPE_ADDER
    ),

  getCostingFieldValue: (
    bomPart: IBomPart,
    fieldType: string,
    costingType: string
  ): null | number =>
    PricingHelper.get(bomPart?.[BomPartHelper.getCostingFieldName(fieldType, costingType)]),

  getCostingFieldName: (fieldType: string, costingType: string): string => {
    let fieldName = 'computed_';

    switch (costingType) {
      case PricingHelper.PRICING_TYPE_TOTAL:
        fieldName += fieldType;
        break;
      case PricingHelper.PRICING_TYPE_BASE:
      case PricingHelper.PRICING_TYPE_ADDER:
        fieldName += `${fieldType}${upperFirst(costingType)}`;
        break;
      default:
        throw new Error(`Invalid costingType: "${costingType}".`);
    }

    return camelCase(fieldName);
  },

  hasComponentAssigned: (bomPart?: IBomPart) => {
    if (!bomPart) {
      return false;
    }

    return (
      bomPart.type === BomPartType.PART &&
      bomPart.componentId &&
      bomPart.componentName !== COMPONENT_EMPTY_NAME
    );
  },

  getDisplayedPartNumber: (bomPart: IBomPart) => {
    if (1 === bomPart.partArr?.generic) {
      return DEFAULT_PLACEHOLDER;
    }

    if (BomPartType.COMPONENT === bomPart.type) {
      return bomPart.componentName;
    }

    return PartNumberHelper.toLabel(bomPart.partArr?.number);
  },
};
